import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useAsyncBatchedCallback } from "@tanstack/react-pacer";
import type { AxiosInstance } from "axios";
import qs from "qs";
import { EntityType } from "@repo/types/enums";
import type { PagedDataResponse } from "@repo/types/responses";
import type {
  AddUpdateDataViewViewModel,
  GetPagedDataViewModel,
  UpdateDataViewEntityModel,
} from "../../types/viewModels/dataViews";
import type { RecordId, UpdateEntityBatch } from "@repo/types/ui/tables";
import type { ExecutionResponse } from "@repo/types/responses";
import { dataViewKeys } from "@repo/types/queries";
import { useApiAccess } from "@repo/hooks/contexts";
import type { DataViewDTO } from "@/types/dtos";

const useGetDataViews = <T>(entityType: EntityType) => {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: dataViewKeys.listByRelationId({
      entityType: EntityType[entityType],
    }),
    queryFn: async () => {
      const response = await apiClient.get<DataViewDTO<T>[]>(
        `/dataView/getByEntityType?entityType=${entityType}`,
      );
      return response.data;
    },
    enabled: !!entityType,
  });
};

const useGetDataViewById = <T>(id: number | null) => {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: dataViewKeys.details(id ?? 0),
    queryFn: async () => {
      const response = await apiClient.get<DataViewDTO<T>>(
        `/dataView/get/${id}`,
      );
      return response.data;
    },
    enabled: !!id,
  });
};

const useAddUpdateDataView = <T>() => {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (model: AddUpdateDataViewViewModel<T>) => {
      const response = await apiClient.post<ExecutionResponse>(
        "/dataView/addUpdate",
        model,
      );
      return response.data;
    },
    onSuccess: (_, model) => {
      if (model) {
        dataViewKeys.invalidateDetails(queryClient, model.Id!);
        dataViewKeys.invalidateListByRelationId(queryClient, {
          entityType: model.EntityType
            ? EntityType[model.EntityType]
            : undefined,
        });
      }
    },
  });
};

const useGetDataViewEntities = <T>(
  queryKey: QueryKey,
  apiClient: AxiosInstance,
  pagedDataViewModel: GetPagedDataViewModel<T>,
  enabled: boolean = true,
) => {
  return useInfiniteQuery({
    queryKey: queryKey,
    queryFn: async ({ pageParam = 0 }): Promise<PagedDataResponse<T>> => {
      const response = await apiClient.get<PagedDataResponse<T>>(
        `/dataView/get`,
        {
          params: {
            ...pagedDataViewModel,
            Start: pageParam,
          },
          paramsSerializer: {
            serialize: (params) => qs.stringify(params, { allowDots: true }),
          },
        },
      );

      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (_lastGroup, groups) => groups.length,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: enabled && !!pagedDataViewModel.ViewId,
  });
};

const useUpdateDataViewEntities = <T>() => {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  // 🔥 Batch single updates into one API call
  const batchedUpdate = useAsyncBatchedCallback(
    async (items: UpdateDataViewEntityModel<T>[]) => {
      const map = new Map<RecordId, UpdateEntityBatch<T>>();

      for (const item of items) {
        if (!map.has(item.RecordId)) {
          map.set(item.RecordId, {
            RecordId: item.RecordId,
            EntityType: item.EntityType,
            UpdateFields: [],
          });
        }

        const batch = map.get(item.RecordId)!;
        batch.UpdateFields.push({
          ColumnId: item.ColumnId,
          Value: item.Value,
        });
      }

      await apiClient.post<ExecutionResponse>(
        "/dataView/updateEntities",
        Array.from(map.values()),
      );

      //unique set of all query keys to invalidate across all updates in the batch

      for (const queryKey of items.flatMap(
        (item) => item.invalidateQueryKeys,
      )) {
        dataViewKeys.invalidateListByRelationId(queryClient, queryKey);
      }
    },
    {
      wait: 5000, // 5 second batch window
      maxSize: 10, // flush early if 20 edits
    },
  );

  return useMutation<void, Error, UpdateDataViewEntityModel<T>>({
    mutationFn: batchedUpdate,

    onMutate: async (model) => {
      model.invalidateQueryKeys?.forEach(async (key) => {
        await queryClient.cancelQueries({ queryKey: key });

        const previousData = queryClient.getQueryData(key);

        // queryClient.setQueryData(key, (oldData: any) => {
        //   if (!oldData) return oldData;

        //   return {
        //     ...oldData,
        //     pages: [
        //       {
        //         ...oldData.pages[0],
        //         Data: [{ ...model }, ...oldData.pages[0].Data],
        //       },
        //       ...oldData.pages.slice(1),
        //     ],
        //   };
        // });

        return [{ key, previousData }];
      });
    },
  });
};

export {
  useGetDataViews,
  useGetDataViewById,
  useAddUpdateDataView,
  useGetDataViewEntities,
  useUpdateDataViewEntities,
};
